import axios from 'axios'
import { useAuthStore } from '../stores/authStore'
import { useEnvStore } from '../stores/envStore'

export const apiClient = axios.create()

apiClient.interceptors.request.use((config) => {
  config.baseURL = useEnvStore.getState().apiBaseUrl
  const token = useAuthStore.getState().accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

/**
 * Sign-in calls made before a session exists. A 401 from these means a wrong
 * password or an expired `mfaToken` ticket — not an expired access token — so
 * it must reach the login page as-is. Refreshing here would fail (there is no
 * refresh token yet), log out, and hard-reload /login, wiping the error.
 */
const PRE_AUTH_PATHS = ['/admin/login', '/admin/mfa/setup/', '/admin/mfa/verify']

function isPreAuthRequest(url: string | undefined) {
  return !!url && PRE_AUTH_PATHS.some((p) => url.startsWith(p))
}

let isRefreshing = false
let pendingQueue: Array<{
  resolve: (token: string) => void
  reject: (err: unknown) => void
}> = []

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config as typeof error.config & { _retry?: boolean }
    if (error.response?.status !== 401 || original._retry || isPreAuthRequest(original.url)) {
      return Promise.reject(error as unknown)
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (token) => {
            original.headers.Authorization = `Bearer ${token}`
            resolve(apiClient(original))
          },
          reject,
        })
      })
    }

    original._retry = true
    isRefreshing = true

    try {
      const refreshToken = useAuthStore.getState().refreshToken
      const baseURL = useEnvStore.getState().apiBaseUrl
      const { data } = await axios.post(`${baseURL}/admin/refresh-token`, { refreshToken })
      const newToken = data.data.accessToken as string
      useAuthStore.getState().setAccessToken(newToken)
      pendingQueue.forEach((p) => p.resolve(newToken))
      pendingQueue = []
      original.headers.Authorization = `Bearer ${newToken}`
      return apiClient(original)
    } catch (refreshError) {
      pendingQueue.forEach((p) => p.reject(refreshError))
      pendingQueue = []
      useAuthStore.getState().logout()
      window.location.href = '/login'
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  },
)
